import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiErr } from "@/lib/api";
import { Lock, CheckCircle2, ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const tokenKind = sp.get("kind") || "";

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw1.length < 6) return setErr("La password deve avere almeno 6 caratteri.");
    if (pw1 !== pw2) return setErr("Le due password non coincidono.");
    if (!token) return setErr("Token mancante dall'URL.");
    setLoading(true);
    try {
      const endpoint = tokenKind === "access"
        ? "/access-management/consume-reset"
        : "/auth/reset-password";
      await api.post(endpoint, { token, new_password: pw1 });
      setDone(true);
    } catch (e) { setErr(apiErr(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-xl shadow-sm p-8">
          <button
            onClick={() => navigate("/login")}
            className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
            data-testid="reset-back-btn"
          >
            <ArrowLeft size={12} /> Vai al login
          </button>

          {!done ? (
            <>
              <div className="mt-4">
                <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Reimposta accesso</div>
                <h1 className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>Nuova password</h1>
                <p className="text-sm text-slate-500 mt-1">
                  Scegli una nuova password di almeno 8 caratteri, con maiuscola,
                  minuscola, numero e simbolo. Una volta salvata potrai accedere
                  con la nuova password.
                </p>
              </div>

              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-password-form">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Nuova password</Label>
                  <div className="relative mt-1">
                    <Lock size={14} className="absolute left-3 top-3 text-slate-400" />
                    <Input
                      type="password"
                      required
                      value={pw1}
                      onChange={(e) => setPw1(e.target.value)}
                      className="pl-9 h-11"
                      data-testid="reset-pw1-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Ripeti password</Label>
                  <div className="relative mt-1">
                    <Lock size={14} className="absolute left-3 top-3 text-slate-400" />
                    <Input
                      type="password"
                      required
                      value={pw2}
                      onChange={(e) => setPw2(e.target.value)}
                      className="pl-9 h-11"
                      data-testid="reset-pw2-input"
                    />
                  </div>
                </div>

                <Button type="submit" disabled={loading}
                        className="w-full h-11 bg-emerald-800 hover:bg-emerald-700 text-white"
                        data-testid="reset-submit-btn">
                  {loading ? "Salvataggio…" : "Salva nuova password"}
                </Button>

                {err && (
                  <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    {err}
                  </div>
                )}
              </form>
            </>
          ) : (
            <div className="mt-4 text-center space-y-4" data-testid="reset-done-panel">
              <CheckCircle2 size={44} className="mx-auto text-emerald-700" />
              <div className="text-lg font-semibold text-slate-900">Password aggiornata</div>
              <div className="text-sm text-slate-600">Puoi ora accedere con la nuova password.</div>
              <Button onClick={() => navigate("/login")} className="w-full h-11 bg-emerald-800 hover:bg-emerald-700 text-white">
                Vai al login
              </Button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
